import React from 'react';
import clsx from 'clsx';

import {
  type CaraWorkspaceFieldsFragment,
  type CaraWorkspaceMessageFollowUpInput,
  CaraWorkspaceMessageRole,
  CaraWorkspacePlanStageState,
  CaraWorkspacePlanStageType,
  CaraWorkspaceState,
  type HtmlDocumentTemplateFieldsFragment
} from '@oysterjs/ais/api/graphql';

import {
  buildGroups,
  collectUsedFollowUpIds,
  platformUnitsUsedForAgentMessage
} from '@oysterjs/ais/components/workspaces/lib/conversation-groups';
import { getStageEventKind } from '@oysterjs/ais/components/workspaces/lib/stage-event-kind';
import {
  supersededBulkEmailDraftEventIds,
  workspaceBulkSendIds
} from '@oysterjs/ais/components/workspaces/lib/bulk-email-draft-event';
import { FEATURE_FLAGS, useFlag } from '@oysterjs/ais/components/feature-flags';
import { effortRaisedWithinQuick } from '@oysterjs/ais/components/presets/effort-level';

import { EffortRaisedNotice } from './conversation-effort-raised-notice';
import { ResponseMessage } from './conversation-response-message';
import { ArtifactEvent } from './conversation-stage-event';
import { StageEventGroup } from './conversation-stage-event-group';
import { UserMessage } from './conversation-user-message';
import { ProcessAttachmentsStage } from './conversation-process-attachments-stage';
import { useAmsReconciliationEnabled } from './hooks/use-ams-reconciliation-enabled';
import { useBulkEmailArtifactIds } from './hooks/use-bulk-email-artifact-ids';

type StageEventItem = CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];

/**
 * One renderable entry produced by the agent-group → render plan transformation.
 * Consecutive `row`-classified stage events are coalesced into a single
 * `stage-event-group` entry; `artifact`-classified events render standalone
 * (and split surrounding row events into separate groups).
 */
type RenderEntry =
  | { kind: 'user-message'; id: string; message: CaraWorkspaceFieldsFragment['messages'][0] }
  | { kind: 'agent-message'; id: string; message: CaraWorkspaceFieldsFragment['messages'][0] }
  | {
      kind: 'process-attachments';
      id: string;
      stage: CaraWorkspaceFieldsFragment['planStages'][0];
    }
  | { kind: 'stage-event-group'; id: string; events: StageEventItem[] }
  | { kind: 'task-incomplete'; id: string }
  | {
      kind: 'artifact-event';
      id: string;
      event: StageEventItem;
    };

/**
 * Walks an agent group's items and produces a flat render plan, coalescing
 * consecutive row-classified stage events into `stage-event-group` entries
 * and breaking those groups whenever an artifact-producing event appears.
 */
const buildRenderEntries = (
  items: ReturnType<typeof buildGroups>[number]['items'],
  bulkEmailArtifactIds: ReadonlySet<string> | undefined
): RenderEntry[] => {
  const entries: RenderEntry[] = [];
  let buffer: StageEventItem[] = [];
  const bulkEmailDraftCards = bulkEmailArtifactIds && {
    supersededEventIds: supersededBulkEmailDraftEventIds(
      items.filter(
        (item): item is StageEventItem => item.__typename === 'CaraWorkspacePlanStageEvent'
      )
    ),
    emailArtifactIds: bulkEmailArtifactIds
  };

  const flush = () => {
    if (buffer.length === 0) return;
    entries.push({
      kind: 'stage-event-group',
      id: `stage-event-group-${buffer[0]!.id}`,
      events: buffer
    });
    buffer = [];
  };

  items.forEach((item, idx) => {
    if (item.__typename === 'CaraWorkspacePlanStageEvent') {
      const nextItem = items[idx + 1];
      const nextEvent =
        nextItem?.__typename === 'CaraWorkspacePlanStageEvent' ? nextItem : undefined;
      const kind = getStageEventKind(item, nextEvent, bulkEmailDraftCards);

      if (kind === 'row') {
        buffer.push(item);
        return;
      }
      if (kind === 'artifact') {
        flush();
        entries.push({
          kind: 'artifact-event',
          id: `artifact-event-${item.id}`,
          event: item
        });
        return;
      }
      // 'hidden' — skip entirely
      return;
    }

    flush();

    if (item.__typename === 'CaraWorkspaceMessage') {
      entries.push({
        kind: item.role === CaraWorkspaceMessageRole.User ? 'user-message' : 'agent-message',
        id: `message-${item.id}`,
        message: item
      });
      return;
    }

    if (item.__typename === 'CaraWorkspacePlanStage') {
      if (item.type === CaraWorkspacePlanStageType.ProcessAttachments) {
        if (item.state === CaraWorkspacePlanStageState.InProgress) {
          entries.push({ kind: 'process-attachments', id: `pa-${item.id}`, stage: item });
        } else if (item.state === CaraWorkspacePlanStageState.Failed) {
          // Stage boundary only — no visible entry.
        }
        // Success: no visible entry — the stage is in combined only to keep the
        // agent group alive so the "Thinking..." pending fallback can fire.
        return;
      }
      if (
        item.type === CaraWorkspacePlanStageType.Task &&
        item.state === CaraWorkspacePlanStageState.Failed
      ) {
        entries.push({ kind: 'task-incomplete', id: `task-incomplete-${item.id}` });
        return;
      }
    }
  });

  flush();
  return entries;
};

export const Conversation = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  readOnly?: boolean;
  htmlDocumentTemplates?: HtmlDocumentTemplateFieldsFragment[];
  onOpenArtifact?: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
  onSelectFollowUp?: (followUp: CaraWorkspaceMessageFollowUpInput) => void;
}) => {
  const { workspace, htmlDocumentTemplates, onOpenArtifact, onSelectFollowUp } = props;
  const amsReconciliationEnabled = useAmsReconciliationEnabled();

  const groups = React.useMemo(() => buildGroups(workspace), [workspace]);
  const isBulkSendEnabled = useFlag(FEATURE_FLAGS.MAILBOX_BULK_SEND);
  const bulkSendIds = React.useMemo(
    () => (isBulkSendEnabled ? workspaceBulkSendIds(workspace) : []),
    [isBulkSendEnabled, workspace]
  );
  const bulkEmailArtifactIds = useBulkEmailArtifactIds(bulkSendIds);
  const usedFollowUpIds = React.useMemo(() => collectUsedFollowUpIds(workspace), [workspace]);

  // Show a "Thinking..." fallback when the workspace is WORKING but no event in
  // the latest plan stage is currently in-progress — either a gap between events
  // or the stretch before the first one arrives. Suppressed once the agent has
  // started streaming a response message in this turn — at that point the
  // agent is composing its reply, not "thinking", so the fallback would be
  // misleading (and easy to misread, since the response renders below the
  // last stage-event group, which may sit before an artifact card).
  // Also suppressed while any ProcessAttachments stage is in progress — that
  // component already communicates the waiting state, and the Task stage may
  // exist in planStages simultaneously (making a lastPlanStage check fragile).
  const lastPlanStage = workspace.planStages[workspace.planStages.length - 1];
  const lastGroup = groups[groups.length - 1];
  const lastGroupHasAgentMessage =
    lastGroup?.role === CaraWorkspaceMessageRole.Agent &&
    lastGroup.items.some((item) => item.__typename === 'CaraWorkspaceMessage');
  const attachmentsInProgress = workspace.planStages.some(
    (s) =>
      s.type === CaraWorkspacePlanStageType.ProcessAttachments &&
      s.state === CaraWorkspacePlanStageState.InProgress
  );
  const showPendingFallback =
    workspace.state === CaraWorkspaceState.Working &&
    !lastPlanStage?.events.some((e) => !e.endedAt) &&
    !lastGroupHasAgentMessage &&
    !attachmentsInProgress;

  const renderPlans = React.useMemo(
    () =>
      groups.map((group) => ({
        group,
        entries: buildRenderEntries(
          group.items,
          isBulkSendEnabled ? bulkEmailArtifactIds : undefined
        )
      })),
    [groups, isBulkSendEnabled, bulkEmailArtifactIds]
  );

  const lastPlanIndex = renderPlans.length - 1;

  // The notice belongs directly under the request whose turn raised the level,
  // rather than wherever the conversation has got to now. A raise before the
  // first turn names no message, and belongs under the first request.
  const effortRaisedPlanIndex = React.useMemo(() => {
    if (!workspace.effortRaisedReason) return -1;
    const raisedBy = workspace.effortRaisedMessageId;
    if (!raisedBy) return 0;
    return renderPlans.findIndex(({ group }) =>
      group.items.some((item) => item.__typename === 'CaraWorkspaceMessage' && item.id === raisedBy)
    );
  }, [renderPlans, workspace.effortRaisedReason, workspace.effortRaisedMessageId]);

  // Identify the stage-event group the pending fallback attaches to (and trails a
  // thinking placeholder inside). Only the final plan is searched: a match further
  // back belongs to a finished turn and renders above the user message that started
  // the current one, which would put "Thinking..." in the wrong turn.
  const lastStageGroupAddress = React.useMemo(() => {
    const entries = renderPlans[lastPlanIndex]?.entries ?? [];
    for (let j = entries.length - 1; j >= 0; j--) {
      if (entries[j]!.kind === 'stage-event-group') return { i: lastPlanIndex, j };
    }
    return null;
  }, [renderPlans, lastPlanIndex]);

  // Where the synthetic pending group goes when no stage-event group exists to
  // absorb it. Until the agent emits its first event or token the final group is
  // the user's message, leaving nothing to hang the indicator inside — so it
  // renders as its own group instead. Focused mode occupies that window for as long
  // as it takes to read the inline documents and reach the first token, and can
  // finish a turn without ever emitting a stage event.
  const trailingPending: 'none' | 'in-last-group' | 'standalone' =
    !showPendingFallback || lastStageGroupAddress
      ? 'none'
      : renderPlans[lastPlanIndex]?.group.role === CaraWorkspaceMessageRole.Agent
        ? 'in-last-group'
        : 'standalone';

  // Identify the message currently receiving tokens: the last agent message of the last
  // plan. Citation resolution is held off until it finishes so a half-written footnote URL
  // never becomes a clickable destination.
  const streamingMessageAddress = React.useMemo(() => {
    if (workspace.state !== CaraWorkspaceState.Working) return null;
    const i = renderPlans.length - 1;
    const entries = renderPlans[i]?.entries ?? [];
    for (let j = entries.length - 1; j >= 0; j--) {
      if (entries[j]!.kind === 'agent-message') return { i, j };
    }
    return null;
  }, [renderPlans, workspace.state]);

  return (
    <div className="flex flex-col gap-10">
      {renderPlans.map(({ group, entries }, i) => {
        const groupKey = `${group.role}-${group.items[0]?.id ?? i}`;

        const needsTrailingPending = trailingPending === 'in-last-group' && i === lastPlanIndex;
        const effortLevelRaised = i === effortRaisedPlanIndex;

        return (
          <div
            key={groupKey}
            className={clsx(
              'flex flex-col',
              '[&>[data-conversation-role]]:mt-0',
              '[&>[data-conversation-role="user-message"]+[data-conversation-role="user-message"]]:mt-3',
              '[&>[data-conversation-role="agent-message"]+[data-conversation-role="stage-event"]]:mt-0',
              '[&>[data-conversation-role="agent-message"]+[data-conversation-role="stage-event"]]:mt-6',
              '[&>[data-conversation-role="agent-message"]+[data-conversation-role="artifact-card"]]:mt-3',
              '[&>[data-conversation-role="artifact-card"]+[data-conversation-role="agent-message"]]:mt-3',
              '[&>[data-conversation-role="artifact-card"]+[data-conversation-role="artifact-card"]]:mt-3',
              '[&>[data-conversation-role="artifact-card"]+[data-conversation-role="stage-event"]]:mt-3',
              '[&>[data-conversation-role="stage-event"]+[data-conversation-role="agent-message"]]:mt-3',
              '[&>[data-conversation-role="stage-event"]+[data-conversation-role="stage-event"]]:mt-3',
              '[&>[data-conversation-role="stage-event"]+[data-conversation-role="artifact-card"]]:mt-3',
              '[&>[data-conversation-role="user-message"]+[data-conversation-role="effort-raised-notice"]]:mt-3'
            )}
          >
            {entries.map((entry, j) => {
              if (entry.kind === 'user-message') {
                return (
                  <UserMessage
                    key={entry.id}
                    messageId={entry.message.id}
                    content={entry.message.content}
                    attachments={entry.message.files}
                    amsContext={entry.message.amsContext}
                    amsRefs={entry.message.amsRefs}
                    workspaceId={workspace.id}
                    readOnly={props.readOnly}
                    attachmentsProcessing={attachmentsInProgress}
                    amsReconciliationEnabled={amsReconciliationEnabled}
                  />
                );
              }

              if (entry.kind === 'agent-message') {
                // The "is last response message" flag was historically used to keep
                // the latest assistant message styled distinctly. Preserve that by
                // checking if this is the first agent-message entry in the very
                // last agent group.
                const isLast =
                  i === renderPlans.length - 1 &&
                  j === entries.findIndex((e) => e.kind === 'agent-message');
                const latestMessage = workspace.messages[workspace.messages.length - 1];
                const isStreamingThisMessage =
                  workspace.state === CaraWorkspaceState.Working &&
                  latestMessage?.id === entry.message.id &&
                  latestMessage?.role === CaraWorkspaceMessageRole.Agent;
                // The spinner means "more may still land in this message." Once later
                // stage events or artifacts appear in the same turn, progress belongs on
                // the timeline below, not under an earlier paragraph.
                const hasLaterEntriesInGroup = j < entries.length - 1;
                return (
                  <ResponseMessage
                    key={entry.id}
                    htmlDocumentTemplates={htmlDocumentTemplates || []}
                    message={entry.message}
                    workspaceId={workspace.id}
                    platformUnitsUsed={platformUnitsUsedForAgentMessage(
                      workspace,
                      entry.message.id
                    )}
                    onSelectFollowUp={onSelectFollowUp || (() => {})}
                    usedFollowUpIds={usedFollowUpIds}
                    isLastItem={isLast}
                    loading={isStreamingThisMessage && !hasLaterEntriesInGroup}
                    readOnly={props.readOnly}
                    streaming={streamingMessageAddress?.i === i && streamingMessageAddress?.j === j}
                    amsReconciliationEnabled={amsReconciliationEnabled}
                  />
                );
              }

              if (entry.kind === 'task-incomplete') {
                return (
                  <div
                    key={entry.id}
                    role="status"
                    aria-live="polite"
                    data-conversation-role="task-incomplete"
                    className="border-l-2 border-amber-500 pl-4 text-sm text-slate-600 dark:text-slate-300"
                  >
                    This request did not complete.
                    {!props.readOnly && ' You can send another message to try again.'}
                  </div>
                );
              }

              if (entry.kind === 'process-attachments') {
                return <ProcessAttachmentsStage key={entry.id} stage={entry.stage} />;
              }

              if (entry.kind === 'stage-event-group') {
                const isLastStageGroup =
                  lastStageGroupAddress != null &&
                  lastStageGroupAddress.i === i &&
                  lastStageGroupAddress.j === j;
                return (
                  <StageEventGroup
                    key={entry.id}
                    events={entry.events}
                    pendingFallback={isLastStageGroup && showPendingFallback}
                  />
                );
              }

              if (entry.kind === 'artifact-event') {
                return (
                  <ArtifactEvent
                    key={entry.id}
                    workspace={workspace}
                    htmlDocumentTemplates={htmlDocumentTemplates || []}
                    event={entry.event}
                    setShowArtifactFocusDialog={onOpenArtifact || (() => {})}
                  />
                );
              }

              return null;
            })}

            {needsTrailingPending && (
              <StageEventGroup events={[]} pendingFallback key="__pending__" />
            )}
            {effortLevelRaised && (
              <EffortRaisedNotice withinQuick={effortRaisedWithinQuick(workspace)} />
            )}
          </div>
        );
      })}

      {trailingPending === 'standalone' && (
        <div className="flex flex-col">
          <StageEventGroup events={[]} pendingFallback />
        </div>
      )}
    </div>
  );
};
